import { useState, useRef, useEffect } from 'react';
import {
	Spacer,
	Label,
	Radio,
	Button,
	Text,
	SmallDialog,
	H2,
	Tile,
	InfoChip,
	toast,
	Tooltip,
	FuelLevyHistoryPanel,
	Select
} from '@components';
import styles from './FuelLevySettingsPanel.module.scss';
import {
	RateCardSettingsFuelLevies,
	RateCardSettings
} from '@state';
import { useRecoilState } from 'recoil';
import { useFeatureFlag, useIsInRoute, useRateCardFuelLeviesState, useRateCardState } from '@hooks';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import {
	BUTTON_SIZE, BUTTON_TYPES, CHIP_TYPE, SPACER_DIRECTION, TEXT_WEIGHT, TFuelLevyType, TOOLTIP_POSITION, TRateCardFuelLevy
} from '@types';
import { ChangeEvent } from 'react';
import {
	capitalizeFirstLetter, formatDate, formatFuelLevyPercent, getLaneFuelLevyId,
	getSelectedFuelLevy, parseAndFormatDate, replaceSelectedFuelLevy
} from '@utils';
import { DeleteOutlined, EditOutlined } from '@mui/icons-material';
import { useDeleteScheduledFuelLevy } from '@api';
import { ScheduleFuelLevyModal } from './ScheduleFuelLevyModal';

type TFuelLevySettingsPanelProps = {
	isDisabled?: boolean;
	isLinkedRateCard?: boolean;
}

export const FuelLevySettingsPanel = (props: TFuelLevySettingsPanelProps) => {
	const { isDisabled, isLinkedRateCard } = props;

	const carrierInRoute = useIsInRoute('carrier');
	const shipperInRoute = useIsInRoute('shipper');
	const settingsInRoute = useIsInRoute('settings');
	const isLinkedRateCardStep3 = useIsInRoute('step3') && isLinkedRateCard;

	// Use smart hooks for context-aware state or settings state
	const [rateCardFuelLevies, setRateCardFuelLevies] = useRateCardFuelLeviesState();
	const [settingsFuelLevies, setSettingsFuelLevies] = useRecoilState(RateCardSettingsFuelLevies);
	const [rcFuelLevies, setRcFuelLevies] = settingsInRoute
		? [settingsFuelLevies, setSettingsFuelLevies]
		: [rateCardFuelLevies, setRateCardFuelLevies];

	const [rcSettings, setRcSettings] = useRecoilState(RateCardSettings);
	const [rateCard, setRateCard] = useRateCardState();

	const [showCancelModal, setShowCancelModal] = useState(false);
	const [showChangeModal, setShowChangeModal] = useState(false);
	const [showHistoryPanel, setShowHistoryPanel] = useState(false);
	const fuelLevyHistoryFeatureFlag = useFeatureFlag('fuel_levy_history');
	const [editingFuelLevyType, setEditingFuelLevyType] = useState<'next' | 'current' | null>(null);
	const { callDeleteScheduledFuelLevy } = useDeleteScheduledFuelLevy();
	const defaultFuelLevyType = shipperInRoute || isLinkedRateCardStep3 ? 'global_shipper' : 'global_carrier';

	const newType = useRef({
		value: defaultFuelLevyType,
		label: 'global',
	});
	const [fuelLevyType, setFuelLevyType] = useState(settingsInRoute ? rcSettings?.settings.fuel_levy_type || defaultFuelLevyType : rateCard?.fuel_levy_type as TFuelLevyType);

	const customerFuelLevyFeatureFlag = useFeatureFlag('customer_fuel_levy');
	const customerLevies = rcFuelLevies?.customer_levies ?? [];
	const selectedCustomerLevyId = settingsInRoute
		? rcSettings?.settings.fuel_levy_id
		: getLaneFuelLevyId(rateCard);
	const selectedFuelLevy = getSelectedFuelLevy(rcFuelLevies, fuelLevyType, selectedCustomerLevyId);
	const customerLabel = shipperInRoute || isLinkedRateCardStep3 ? 'Shipper' : 'Carrier';

	const updateSelectedFuelLevy = (levy: TRateCardFuelLevy | null) => {
		setRcFuelLevies(replaceSelectedFuelLevy(rcFuelLevies, fuelLevyType, selectedCustomerLevyId, levy));
	}

	const triggerUpdateFuelLevyType = (e: ChangeEvent<HTMLInputElement>) => {
		const { value, name } = e.target;
		switch (name) {
		case 'customer':
			newType.current = {
				value,
				label: shipperInRoute || isLinkedRateCardStep3 ? 'shipper' : 'carrier'
			}
			break;
		case 'rate_card':
			newType.current = {
				value,
				label: 'lane'
			}
			break;
		case 'global_shipper':
		case 'global_carrier':
		default:
			newType.current = {
				value,
				label: 'global',
			}
		}
		setShowChangeModal(true);
	}
	const selectCustomerFuelLevy = (fuelLevyId: string) => {
		if (settingsInRoute) {
			if (!rcSettings) return;
			setRcSettings({ ...rcSettings, settings: { ...rcSettings.settings, fuel_levy_id: fuelLevyId } });
			return;
		}
		if (!rateCard) return;
		setRateCard({ ...rateCard, fuel_levy_id: fuelLevyId });
	}

	const triggerChangeFuelLevyType = () => {
		if (settingsInRoute && rcSettings) {
			const update = {
				...rcSettings,
				settings: {
					...rcSettings?.settings,
					fuel_levy_type: newType.current.value as TFuelLevyType,
					// The API rejects an id that contradicts the type
					fuel_levy_id: newType.current.value === 'customer' ? rcSettings.settings.fuel_levy_id : undefined
				}
			}
			setRcSettings(update);
			return
		}
		if (rateCard) {
			const update = {
				...rateCard,
				fuel_levy_type: newType.current.value as TFuelLevyType,
				// The API rejects an id that contradicts the type
				fuel_levy_id: newType.current.value === 'customer' ? rateCard.fuel_levy_id : null
			}
			setRateCard(update)
		}
	}

	const hasCurrentTarrif = (levy: TRateCardFuelLevy | null | undefined) => {
		// prevent 0 to return false and check if valid number string
		if (typeof levy?.current?.tariff === 'number') return true;
		if (!levy?.current?.tariff) return false;
		if (!isNaN(Number(levy?.current?.tariff))) return true;
		return false;
	}

	const setNextFuelLevies = () => {
		const fuelLevyToUpdate = hasCurrentTarrif(selectedFuelLevy) ? 'next' : 'current';
		setEditingFuelLevyType(fuelLevyToUpdate);
		updateSelectedFuelLevy({
			...selectedFuelLevy,
			[fuelLevyToUpdate]: {
				...selectedFuelLevy?.[fuelLevyToUpdate],
				start_at: fuelLevyToUpdate === 'current' ? '' : selectedFuelLevy?.[fuelLevyToUpdate]?.start_at || '',
				tariff: (
					fuelLevyToUpdate === 'next' &&
					typeof selectedFuelLevy?.[fuelLevyToUpdate]?.tariff === 'number'
				) ? selectedFuelLevy?.[fuelLevyToUpdate]?.tariff : ''
			}
		} as unknown as TRateCardFuelLevy);
	}
	const updateFuelLevy = (payload: {
		startAt: string,
		tariff: string
	}) => {
		if (!editingFuelLevyType) return;
		const { startAt, tariff } = payload;
		updateSelectedFuelLevy({
			...selectedFuelLevy,
			[editingFuelLevyType]: {
				...selectedFuelLevy?.[editingFuelLevyType],
				start_at: parseAndFormatDate(startAt, 'API_DATE_FORMAT', 'SHORT_DATE'),
				tariff: Number(tariff)
			}
		} as unknown as TRateCardFuelLevy);
		setEditingFuelLevyType(null);
	}
	const isScheduleFuelLevyButtonDisabled = () => {
		if (isDisabled) return true;
		if (fuelLevyType === 'global_shipper') return true;
		if (fuelLevyType === 'global_carrier') return true;
		if (!settingsInRoute && fuelLevyType === 'customer') return true;
		if (selectedFuelLevy?.next?.start_at && typeof selectedFuelLevy?.next?.tariff === 'number') {
			return true;
		}
		if (typeof selectedFuelLevy?.current?.tariff === 'number' && !selectedFuelLevy?.current?.id) {
			return true;
		}
		return false;
	}

	const triggerCancelNextFuelLevies = () => {
		const fuelLevyId = selectedFuelLevy?.next?.id;
		if (fuelLevyId) {
			callDeleteScheduledFuelLevy({ routes: [fuelLevyId] })
				.then((_) => {
					toast.success('Fuel levy successfully deleted');
				})
				.catch((_) => {
					toast.error('There has been an issue deleting the fuel levy.')
					return;
				})
		}
		updateSelectedFuelLevy({ ...selectedFuelLevy, next: null } as TRateCardFuelLevy);
	}

	useEffect(() => {
		if (!editingFuelLevyType) {
			if (!selectedFuelLevy?.next) return;
			let update = { ...selectedFuelLevy };
			if (!selectedFuelLevy?.current?.start_at || typeof selectedFuelLevy?.current?.tariff !== 'number') {
				update = { ...update, current: null } as unknown as TRateCardFuelLevy;
			}
			if (!selectedFuelLevy?.next?.start_at || typeof selectedFuelLevy?.next?.tariff !== 'number') {
				update = { ...update, next: null };
			}
			updateSelectedFuelLevy(update);
		}
	}, [editingFuelLevyType]);

	useEffect(() => {
		setFuelLevyType(settingsInRoute ? rcSettings?.settings.fuel_levy_type || defaultFuelLevyType : rateCard?.fuel_levy_type as TFuelLevyType);
	}, [settingsInRoute, rcSettings?.settings.fuel_levy_type, rateCard?.fuel_levy_type])

	return (
		<Tile stretch>
			<div>
				<div className={styles.upcomingHeader}>
					<H2>
						Fuel levy
					</H2>
					<div className={styles.headerActions}>
						{fuelLevyHistoryFeatureFlag && (
							<Button
								Icon={HistoryOutlinedIcon}
								text={'History'}
								onClick={() => setShowHistoryPanel(!showHistoryPanel)}
								type={BUTTON_TYPES.SECONDARY}
								size={BUTTON_SIZE.SMALL}
								disabled={!selectedFuelLevy?.id}
							/>
						)}
						<Tooltip
							position={TOOLTIP_POSITION.TOP}
							text={'You must save an initial fuel levy before scheduling'}
							visible={typeof selectedFuelLevy?.current?.tariff === 'number' && !selectedFuelLevy?.current?.id}
						>
							<Button
								Icon={CalendarTodayOutlinedIcon}
								text={'Schedule'}
								onClick={setNextFuelLevies}
								type={BUTTON_TYPES.SECONDARY}
								size={BUTTON_SIZE.SMALL}
								disabled={isScheduleFuelLevyButtonDisabled()}
							/>
						</Tooltip>
					</div>
				</div>
				<Spacer size={16} />
				<Label>Type of levy used</Label>
				<Spacer size={5} />
				<Radio.Group className={styles.radioGroup}>
					{rcFuelLevies?.global_shipper &&
						<Radio
							label={`Global ${rcFuelLevies?.global_shipper?.current?.tariff ? ` | ${rcFuelLevies?.global_shipper?.current?.tariff}%` : ''}`}
							name={'global_shipper'}
							value={'global_shipper'}
							onChange={triggerUpdateFuelLevyType}
							checked={settingsInRoute ?
								rcSettings?.settings.fuel_levy_type === 'global_shipper' :
								rateCard?.fuel_levy_type === 'global_shipper'
							}
							disabled={isDisabled}
						/>
					}
					{rcFuelLevies?.global_carrier &&
						<Radio
							label={`Global ${rcFuelLevies?.global_carrier?.current?.tariff ? ` | ${rcFuelLevies?.global_carrier?.current?.tariff}%` : ''}`}
							name={'global_carrier'}
							value={'global_carrier'}
							onChange={triggerUpdateFuelLevyType}
							checked={settingsInRoute ?
								rcSettings?.settings.fuel_levy_type === 'global_carrier' :
								rateCard?.fuel_levy_type === 'global_carrier'
							}
							disabled={isDisabled}
						/>
					}
					<Spacer size={5} />
					{(shipperInRoute || carrierInRoute || isLinkedRateCard) &&
						<Radio
							label={
								customerFuelLevyFeatureFlag ?
									`${customerLabel} specific` :
									`${customerLabel} | 
								${hasCurrentTarrif(customerLevies[0]) ? (`${customerLevies[0]?.current?.tariff}%${
			customerLevies[0]?.current?.start_at ? (` as of ${formatDate(new Date(customerLevies[0].current.start_at), 'SHORT_DATE')}`) : ''}`) : 'Undefined'}`
							}
							name={'customer'}
							value={'customer'}
							onChange={triggerUpdateFuelLevyType}
							checked={settingsInRoute ?
								rcSettings?.settings.fuel_levy_type === 'customer' :
								rateCard?.fuel_levy_type === 'customer'
							}
							disabled={isDisabled || (
								!settingsInRoute && (
									customerFuelLevyFeatureFlag ?
										customerLevies.length === 0 :
										typeof customerLevies[0]?.current?.tariff !== 'number'
								)
							)}
						/>
					}
					<Spacer size={5} />
					{!settingsInRoute &&
						<Radio
							label={`Lane |
								${hasCurrentTarrif(rcFuelLevies?.rate_card) ? (`${rcFuelLevies?.rate_card?.current?.tariff}%${
			rcFuelLevies?.rate_card?.current?.start_at ? (` as of ${formatDate(new Date(rcFuelLevies?.rate_card?.current?.start_at), 'SHORT_DATE')}`) : ''}`) : 'Undefined'}`
							}
							name={'rate_card'}
							value={'rate_card'}
							onChange={triggerUpdateFuelLevyType}
							checked={settingsInRoute ?
								rcSettings?.settings.fuel_levy_type === 'rate_card' :
								rateCard?.fuel_levy_type === 'rate_card'
							}
							disabled={isDisabled}
						/>
					}
					<Spacer size={5} />
				</Radio.Group>
				{customerFuelLevyFeatureFlag && fuelLevyType === 'customer' &&
					<>
						<Spacer size={10} />
						<Select
							name={'customer_fuel_levy'}
							label={`${customerLabel} specific fuel levy`}
							required
							placeholder={'Select a fuel levy'}
							value={selectedCustomerLevyId ? selectedFuelLevy?.id ?? '' : ''}
							onChange={selectCustomerFuelLevy}
							disabled={isDisabled}
							data-testid='@Ofload-FuelLevySettingsPanel-CustomerFuelLevy'
						>
							{customerLevies.map((levy) => (
								<Select.Option value={levy.id} key={levy.id}>
									{`${levy.name ?? customerLabel} | ${formatFuelLevyPercent(levy.current?.tariff)}`}
								</Select.Option>
							))}
						</Select>
					</>
				}
				{
					((
						fuelLevyType === 'customer' && !!selectedFuelLevy && !selectedFuelLevy.current
					)
					|| (
						fuelLevyType === 'rate_card' && !rcFuelLevies?.rate_card?.current
					))
					&& <>
						<Spacer size={10} />
						<InfoChip type={CHIP_TYPE.INFO} text={`You must schedule a fuel levy to activate “${capitalizeFirstLetter(newType.current.label)}” as the fuel levy type`}/>
						<Spacer size={10} />
					</>
				}
				{
					(fuelLevyType !== 'global_carrier' && fuelLevyType !== 'global_shipper') &&
					selectedFuelLevy?.next?.start_at &&
					typeof selectedFuelLevy?.next?.tariff === 'number' && <>
						<Label weight={TEXT_WEIGHT.BOLD}>Scheduled fuel levy</Label>
						<Spacer size={8} />
						<div className={styles.nextFuelLevy}>
							<Text weight={TEXT_WEIGHT.BOLD}>
								Fuel levy:
							</Text>
							<Text>
								{selectedFuelLevy?.next?.tariff}%
							</Text>
							<Spacer dir={SPACER_DIRECTION.VERTICAL} size={8} />
							<Text weight={TEXT_WEIGHT.BOLD}>
								Start date:
							</Text>
							<Text>
								{parseAndFormatDate(selectedFuelLevy?.next?.start_at, 'SHORT_DATE', 'API_DATE_FORMAT')}
							</Text>
							<div className={styles.nextFuelLevyBtnContainer}>
								<Button
									iconOnly
									Icon={EditOutlined}
									onClick={setNextFuelLevies}
									text='Edit'
									type={BUTTON_TYPES.TERTIARY}
									size={BUTTON_SIZE.SMALL}
									disabled={isDisabled}
								/>
								<Button
									iconOnly
									Icon={DeleteOutlined}
									onClick={() => setShowCancelModal(true)}
									text='Delete'
									type={BUTTON_TYPES.TERTIARY}
									size={BUTTON_SIZE.SMALL}
									disabled={isDisabled}
								/>
							</div>
						</div>
					</>
				}
			</div>
			<SmallDialog
				title={'Delete scheduled fuel levy'}
				text={'The current fuel levy will continue to apply.'}
				open={showCancelModal}
				onClose={setShowCancelModal}
				actionButtonFn={triggerCancelNextFuelLevies}
				actionButtonText={'Confirm'}
			/>
			<SmallDialog
				title={`Change to ${capitalizeFirstLetter(newType.current.label)} fuel levy type`}
				text={''}
				open={showChangeModal}
				slot={
					settingsInRoute ?
						newType.current.label === 'global' ? <div>
							<InfoChip type={CHIP_TYPE.WARN} text={`All rate cards using the ${shipperInRoute || isLinkedRateCardStep3 ? 'Shipper' : 'Carrier'} fuel levy will be changed to Global.`} />
							<Spacer size={8} />
							<Text>
								Please note this will override the current fuel levy and start from today onwards.
							</Text>
						</div> :
							<Text>
							Please note this will override the current fuel levy. You must schedule a fuel levy to activate.
							</Text> :
						newType.current.label === 'lane' ?
							<Text>
							Please note this will override the current fuel levy. You must schedule a lane fuel levy to activate.
							</Text> :
							newType.current.label === 'global' ?
								<Text>
							Please note this will override the current fuel levy and start from today onwards.
								</Text> :
								<Text>
							Please note this will override the current fuel levy and start from today onwards
								</Text>
				}
				onClose={setShowChangeModal}
				actionButtonFn={triggerChangeFuelLevyType}
				actionButtonText={'Confirm'}
			/>
			{editingFuelLevyType &&
				<ScheduleFuelLevyModal
					isFirstSchedule={!hasCurrentTarrif(selectedFuelLevy)}
					settingsInRoute={settingsInRoute}
					editingFuelLevyType={editingFuelLevyType}
					startAt={parseAndFormatDate(`${selectedFuelLevy?.[editingFuelLevyType]?.start_at}`)}
					tariff={`${typeof selectedFuelLevy?.[editingFuelLevyType]?.tariff === 'number' ? selectedFuelLevy?.[editingFuelLevyType]?.tariff : ''}`}
					newTypeLabel={newType.current.label}
					onConfirmFuelLevyUpdate={updateFuelLevy}
					onCloseDialog={() => setEditingFuelLevyType(null)}
				/>
			}
			{showHistoryPanel && rateCard?.external_id &&
				<FuelLevyHistoryPanel
					rateCardExternalId={rateCard.external_id}
					isOpen={showHistoryPanel}
					onClose={() => setShowHistoryPanel(false)}
					customerName={rateCard?.customer_name}
					customerType={shipperInRoute || isLinkedRateCardStep3 ? 'shipper' : carrierInRoute ? 'carrier' : 'admin'}
				/>
			}
		</Tile>
	);
}
